// Path: ./src/app/pages/CreateBannerPage/useTranslateBannerContent.ts
import { useState } from "react";
import {
  CreateGfBannerDto,
  TranslationResultDto,
} from "@swagger/typescript-fetch-goflyer";
import i18n from "i18next";

import { messages } from "../../../locales/messages";
import { adminApiService } from "../../../utils/api";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import {
  bannerFieldLimit,
  BannerContentField,
  BannerLocaleSuffix,
  bannerContentField,
} from "../../../utils/banner/bannerFields";
import {
  BANNER_TRANSLATE_SOURCE_LANG,
  BANNER_TRANSLATE_TARGET_LANG,
  BannerTranslatableSuffix,
  bannerFieldsNeedingTranslation,
} from "../../../utils/banner/bannerTranslate";

interface Params {
  banner?: CreateGfBannerDto;
  suffix: BannerLocaleSuffix;
  onFieldChange: (field: string, value: string) => void;
}

export interface TranslateBannerContent {
  canTranslate: boolean;
  emptyFields: BannerContentField[];
  pendingFields: BannerContentField[];
  isTranslatingAll: boolean;
  translateField: (base: BannerContentField) => Promise<void>;
  translateEmptyFields: () => Promise<void>;
}

export const useTranslateBannerContent = ({
  banner,
  suffix,
  onFieldChange,
}: Params): TranslateBannerContent => {
  const authHeaders = useAuthHeaders();
  const [pendingFields, setPendingFields] = useState<BannerContentField[]>([]);
  const [isTranslatingAll, setIsTranslatingAll] = useState(false);

  const canTranslate = suffix !== "";
  const targetSuffix = suffix as BannerTranslatableSuffix;
  const record = banner as Record<string, unknown> | undefined;

  const emptyFields = canTranslate
    ? bannerFieldsNeedingTranslation(record, targetSuffix)
    : [];

  const englishValue = (base: BannerContentField): string =>
    String(record?.[base] ?? "").trim();

  const requestTranslation = async (
    text: string,
  ): Promise<string | undefined> => {
    const response =
      await adminApiService.gfMiscellaneousAdminControllerTranslateTextByGoogle(
        {
          text,
          sourceLang: BANNER_TRANSLATE_SOURCE_LANG,
          targetLang: BANNER_TRANSLATE_TARGET_LANG[targetSuffix],
        },
        authHeaders,
      );

    const result = (response as TranslationResultDto | undefined)?.result;
    return result?.trim() || undefined;
  };

  const applyTranslation = (
    base: BannerContentField,
    value: string,
  ): boolean => {
    onFieldChange(bannerContentField(base, targetSuffix), value);
    return value.length > bannerFieldLimit(base, targetSuffix);
  };

  const translateField = async (base: BannerContentField) => {
    const source = englishValue(base);
    if (!canTranslate || !source || pendingFields.includes(base)) return;

    setPendingFields((prev) => [...prev, base]);
    try {
      const translated = await requestTranslation(source);
      if (!translated) return;
      if (applyTranslation(base, translated)) {
        toastError(i18n.t(messages.banner_translate_over_limit()));
      }
    } finally {
      setPendingFields((prev) => prev.filter((field) => field !== base));
    }
  };

  const translateEmptyFields = async () => {
    if (!canTranslate || isTranslatingAll) return;

    const targets = bannerFieldsNeedingTranslation(record, targetSuffix);
    if (!targets.length) {
      toastError(i18n.t(messages.banner_translate_nothing_to_do()));
      return;
    }

    setIsTranslatingAll(true);
    setPendingFields(targets);
    let filled = 0;
    let overLimit = 0;
    try {
      for (const base of targets) {
        const translated = await requestTranslation(englishValue(base));
        if (!translated) continue;
        filled += 1;
        if (applyTranslation(base, translated)) overLimit += 1;
      }
    } finally {
      setPendingFields([]);
      setIsTranslatingAll(false);
    }

    if (filled) {
      toastSuccess(
        i18n.t(messages.banner_translate_filled(), { count: filled }),
      );
    }
    if (overLimit) {
      toastError(i18n.t(messages.banner_translate_over_limit()));
    }
  };

  return {
    canTranslate,
    emptyFields,
    pendingFields,
    isTranslatingAll,
    translateField,
    translateEmptyFields,
  };
};
